import {DEFAULT_AVAILABILITY_OPTIONS, InputTime} from '@/components/commons/input-time/InputTime'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {FC, useState, useEffect, memo, useCallback} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {StyledTimeInput} from './style'
import {
    DEFAULT_START_TIME,
    DEFAULT_END_TIME,
    DEFAULT_TIME_INTERVAL,
    FIELD_OPTIONS,
    timeToMinutes
} from '@/features/users-availability/utils'

interface TimeSlotProps {
    dayName: string
    index: number
    onRemove: () => void
    showRemoveButton: boolean
}

export const TimeSlot: FC<TimeSlotProps> = memo(({dayName, index, onRemove, showRemoveButton}) => {
    const {t} = useTranslation()
    const {control, trigger, setValue} = useFormContext()
    const [availableEndTimes, setAvailableEndTimes] = useState<string[]>([])

    // Watch the start and end time values
    const startTime = useWatch({
        control,
        name: `${dayName}.timeSlots.${index}.start`
    })

    const endTime = useWatch({
        control,
        name: `${dayName}.timeSlots.${index}.end`
    })

    // Handle start time change
    const handleStartTimeChange = useCallback(
        (value: string | null) => {
            if (!value) return

            const fieldNameStart = `${dayName}.timeSlots.${index}.start`
            const fieldNameEnd = `${dayName}.timeSlots.${index}.end`
            const fieldNameSlots = `${dayName}.timeSlots`

            setValue(fieldNameStart, value, FIELD_OPTIONS)

            if (endTime) {
                const startTotalMinutes = timeToMinutes(value)
                const endTotalMinutes = timeToMinutes(endTime)

                if (startTotalMinutes >= endTotalMinutes) {
                    setValue(fieldNameEnd, '', FIELD_OPTIONS)
                }
            }

            void trigger(fieldNameSlots)
        },
        [dayName, index, setValue, trigger, endTime]
    )

    // Handle end time change
    const handleEndTimeChange = useCallback(
        (value: string | null) => {
            if (!value) return

            const fieldNameEnd = `${dayName}.timeSlots.${index}.end`
            const fieldNameSlots = `${dayName}.timeSlots`

            setValue(fieldNameEnd, value, FIELD_OPTIONS)
            void trigger(fieldNameSlots)
        },
        [dayName, index, setValue, trigger]
    )

    // Update available end times whenever start time changes
    useEffect(() => {
        if (!startTime) {
            setAvailableEndTimes(DEFAULT_AVAILABILITY_OPTIONS)
            return
        }

        const startTotalMinutes = timeToMinutes(startTime)

        const validEndTimes = DEFAULT_AVAILABILITY_OPTIONS.filter(time => {
            return timeToMinutes(time) > startTotalMinutes
        })

        setAvailableEndTimes(validEndTimes)
    }, [startTime])

    return (
        <Flexbox gap={2} align="center">
            <StyledTimeInput>
                <Controller
                    control={control}
                    name={`${dayName}.timeSlots.${index}.start`}
                    render={({field, fieldState}) => (
                        <InputTime
                            value={field.value || ''}
                            onChange={handleStartTimeChange}
                            errorMessage={fieldState.error?.message}
                            startTime={DEFAULT_START_TIME}
                            endTime={DEFAULT_END_TIME}
                            interval={DEFAULT_TIME_INTERVAL}
                            placeholder={t('commons:select')}
                            availableSlots={DEFAULT_AVAILABILITY_OPTIONS}
                        />
                    )}
                />
            </StyledTimeInput>
            <span>-</span>
            <StyledTimeInput>
                <Controller
                    control={control}
                    name={`${dayName}.timeSlots.${index}.end`}
                    render={({field, fieldState}) => (
                        <InputTime
                            value={field.value || ''}
                            onChange={handleEndTimeChange}
                            errorMessage={fieldState.error?.message}
                            startTime={startTime || DEFAULT_START_TIME}
                            endTime={DEFAULT_END_TIME}
                            interval={DEFAULT_TIME_INTERVAL}
                            placeholder={t('commons:select')}
                            availableSlots={availableEndTimes}
                            disabled={!startTime}
                        />
                    )}
                />
            </StyledTimeInput>
            {showRemoveButton && (
                <Button variant="ghost" shape="square" onClick={onRemove} size="md">
                    <Trash01Icon />
                </Button>
            )}
        </Flexbox>
    )
})

// Add display name
TimeSlot.displayName = 'TimeSlot'
